import React from 'react'
import {
  ArrayParam,
  BooleanParam,
  DecodedValueMap,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault
} from 'use-query-params'
import { UrlUpdateType } from 'use-query-params/lib/types'
import {
  AnalyticsFilterDefaultValues,
  AnalyticsFilterType
} from '../AnalyticsContext'

const QUERY_FILTER_CONFIG = {
  includeAdmins: withDefault(
    BooleanParam,
    AnalyticsFilterDefaultValues.includeAdmins
  ),
  includeDrafts: withDefault(
    BooleanParam,
    AnalyticsFilterDefaultValues.includeDrafts
  ),
  includeArchived: withDefault(
    BooleanParam,
    AnalyticsFilterDefaultValues.includeArchived
  ),
  workspaceIds: withDefault(
    ArrayParam,
    AnalyticsFilterDefaultValues.workspaceIds
  ),
  pathwayIds: withDefault(ArrayParam, AnalyticsFilterDefaultValues.pathwayIds),
  campaignIds: withDefault(
    ArrayParam,
    AnalyticsFilterDefaultValues.campaignIds
  ),
  groupIds: withDefault(ArrayParam, AnalyticsFilterDefaultValues.groupIds),
  page: withDefault(NumberParam, AnalyticsFilterDefaultValues.page),
  search: withDefault(StringParam, AnalyticsFilterDefaultValues.search)
}

type AnalyticsFilterConfig = typeof QUERY_FILTER_CONFIG
type DecodedAnalyticsFilters = DecodedValueMap<AnalyticsFilterConfig>
export type SetAnalyticsQuery = (
  changes: Partial<AnalyticsFilterType>,
  urlUpdateType?: UrlUpdateType
) => void

const normalizeFilterFromQuery = (filter: DecodedAnalyticsFilters) => {
  return {
    ...filter,
    campaignIds: filter.campaignIds
      ? filter.campaignIds.map((value) => Number(value))
      : null,
    workspaceIds: filter.workspaceIds
      ? filter.workspaceIds.map((value) => Number(value))
      : null,
    pathwayIds: filter.pathwayIds
      ? filter.pathwayIds.map((value) => Number(value))
      : null,
    groupIds: filter.groupIds
      ? filter.groupIds.map((value) => Number(value))
      : null
  }
}

const transformFiltersToQueryStringTypes = (
  filterParams: AnalyticsFilterType
) => {
  return {
    ...filterParams,
    campaignIds: filterParams.campaignIds?.map((value) => value.toString()),
    workspaceIds: filterParams.workspaceIds?.map((value) => value.toString()),
    pathwayIds: filterParams.pathwayIds?.map((value) => value.toString()),
    groupIds: filterParams.groupIds?.map((value) => value.toString())
  }
}

export const useAnalyticsFilterFromQuery = (): [
  AnalyticsFilterType,
  SetAnalyticsQuery
] => {
  const [filterFromQuery, setFilter] = useQueryParams(QUERY_FILTER_CONFIG)

  const filter = React.useMemo(
    () => normalizeFilterFromQuery(filterFromQuery),
    [filterFromQuery]
  )

  const changeFilter = React.useCallback(
    (changes: Partial<AnalyticsFilterType>, urlUpdateType?: UrlUpdateType) => {
      setFilter(
        (currentFilters) =>
          transformFiltersToQueryStringTypes({
            ...normalizeFilterFromQuery(currentFilters),
            ...changes
          }),
        urlUpdateType
      )
    },
    []
  )

  return [filter, changeFilter]
}
